import { useState } from "react";
import { Bot, MessageSquare, Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks";
import { cn } from "@/lib";
import {
  BillingAPI,
  type Metric,
  type PackSize,
} from "@/services/billing";

// Mirror of apps/api/config/credit_packs.php — keep in sync if the SKU table
// changes there. The modal renders Small / Medium / Large per metric.
const PACK_SKUS: Record<
  Metric,
  Record<PackSize, { amount: number; price_brl: number }>
> = {
  ai_conversations: {
    small: { amount: 500, price_brl: 30 },
    medium: { amount: 2000, price_brl: 100 },
    large: { amount: 10000, price_brl: 400 },
  },
  ai_messages: {
    small: { amount: 1000, price_brl: 100 },
    medium: { amount: 5000, price_brl: 400 },
    large: { amount: 25000, price_brl: 1500 },
  },
};

const SIZES: PackSize[] = ["small", "medium", "large"];

const SIZE_LABEL: Record<PackSize, string> = {
  small: "Pequeno",
  medium: "Médio",
  large: "Grande",
};

const METRIC_LABEL_PLURAL: Record<Metric, string> = {
  ai_conversations: "conversas IA",
  ai_messages: "mensagens IA",
};

const METRIC_ICONS: Record<Metric, typeof Bot> = {
  ai_conversations: Bot,
  ai_messages: MessageSquare,
};

function formatInt(value: number): string {
  return value.toLocaleString("pt-BR");
}

function formatBrl(value: number): string {
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

interface BuyCreditsModalProps {
  open: boolean;
  metric: Metric | null;
  periodEnd?: string | null;
  onOpenChange: (open: boolean) => void;
}

export function BuyCreditsModal({
  open,
  metric,
  periodEnd,
  onOpenChange,
}: BuyCreditsModalProps) {
  const { toast } = useToast();
  const [purchasing, setPurchasing] = useState<PackSize | null>(null);

  const skus = metric ? PACK_SKUS[metric] : null;
  const Icon = metric ? METRIC_ICONS[metric] : Bot;
  const pluralLabel = metric ? METRIC_LABEL_PLURAL[metric] : "créditos";
  const validityDate = formatDate(periodEnd);

  async function handleSelect(size: PackSize) {
    if (!metric || purchasing) return;
    setPurchasing(size);
    try {
      const { payment_link } = await BillingAPI.purchaseCredits(metric, size);
      if (!payment_link) {
        throw new Error("Resposta sem link de pagamento.");
      }
      window.location.href = payment_link;
    } catch {
      setPurchasing(null);
      toast({
        title: "Erro ao comprar créditos",
        description:
          "Não conseguimos gerar o link de pagamento. Tente novamente em instantes.",
        variant: "destructive",
      });
    }
  }

  return (
    <Dialog
      open={open && metric !== null}
      onOpenChange={(o) => {
        if (purchasing) return;
        onOpenChange(o);
      }}
    >
      <DialogContent
        data-testid="buy-credits-modal"
        className="sm:max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-teal-500" />
            Comprar créditos extras de {pluralLabel}
          </DialogTitle>
          <DialogDescription>
            Escolha um pacote. O pagamento é processado por nosso parceiro de
            pagamentos e você é redirecionado para concluir a compra.
          </DialogDescription>
        </DialogHeader>

        {skus && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {SIZES.map((size) => {
              const sku = skus[size];
              const isPurchasing = purchasing === size;
              const isHighlighted = size === "medium";
              return (
                <button
                  key={size}
                  type="button"
                  data-sku={size}
                  data-metric={metric}
                  disabled={purchasing !== null}
                  onClick={() => handleSelect(size)}
                  className={cn(
                    "flex flex-col items-start rounded-2xl border bg-white p-4 text-left shadow-sm transition hover:border-teal-500 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 disabled:cursor-not-allowed disabled:opacity-60",
                    isHighlighted
                      ? "border-teal-500 ring-1 ring-teal-500/30"
                      : "border-border"
                  )}
                >
                  {isHighlighted && (
                    <span className="mb-2 inline-flex w-fit rounded-full bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700">
                      Mais popular
                    </span>
                  )}
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">
                    {SIZE_LABEL[size]}
                  </span>
                  <span className="mt-1 text-xl font-bold text-foreground">
                    {formatInt(sku.amount)}{" "}
                    <span className="text-sm font-normal text-muted-foreground">
                      {pluralLabel}
                    </span>
                  </span>
                  <span className="mt-2 text-lg font-semibold text-teal-600">
                    {formatBrl(sku.price_brl)}
                  </span>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    {isPurchasing ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Gerando link...
                      </>
                    ) : (
                      "Selecionar"
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <p className="mt-1 text-xs text-muted-foreground">
          {validityDate
            ? `Créditos válidos até ${validityDate} (fim do ciclo atual). Saldo não usado expira.`
            : "Créditos válidos até o fim do ciclo atual. Saldo não usado expira."}
        </p>
      </DialogContent>
    </Dialog>
  );
}
